/**
 * Copyright (c) 2026 Accunai India Services Pvt. Ltd. All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This file is part of proprietary software owned by Accunai India Services Pvt. Ltd.
 * Unauthorized copying, distribution, modification, or use of this file,
 * in whole or in part, via any medium, is strictly prohibited without
 * prior written permission from Accunai India Services Pvt. Ltd.
 *
 * Generated with GitHub Copilot Business/Enterprise.
 * Reviewed and approved by: AI-assisted / 2026-07-09
 */

import React, { useEffect, useState } from "react";
import KeyboardDoubleArrowLeftRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowLeftRounded";
import KeyboardDoubleArrowRightRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowRightRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import TerminalRoundedIcon from "@mui/icons-material/TerminalRounded";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import Logo from "../shared/ui/components/Logo";
import styles from "./SidebarLayout.module.css";

type NavItem = {
  key: string;
  label: string;
  to: string;
  icon: React.ReactNode;
};

const navItems: NavItem[] = [
  {
    key: "dashboard",
    label: "Dashboard",
    to: "/dashboard",
    icon: <DashboardRoundedIcon fontSize="small" />,
  },
  {
    key: "projects",
    label: "Projects",
    to: "/projects",
    icon: <DescriptionRoundedIcon fontSize="small" />,
  },
  {
    key: "users",
    label: "Admins",
    to: "/users",
    icon: <PersonRoundedIcon fontSize="small" />,
  },
  {
    key: "experts",
    label: "Experts",
    to: "/experts",
    icon: <GroupRoundedIcon fontSize="small" />,
  },
  {
    key: "vendors",
    label: "Vendors",
    to: "/vendors",
    icon: <StorefrontRoundedIcon fontSize="small" />,
  },
  {
    key: "script-execution",
    label: "Script Execution",
    to: "/script-execution",
    icon: <TerminalRoundedIcon fontSize="small" />,
  },
];

const getActiveKey = (pathname: string): string => {
  if (pathname.includes("dashboard")) {
    return "dashboard";
  }

  if (
    pathname.includes("projects") ||
    pathname.includes("addproject") ||
    pathname.includes("project/") ||
    pathname.includes("bulkupload") ||
    pathname.includes("addconfig") ||
    pathname.includes("uploadtask") ||
    pathname.includes("allocation") ||
    pathname.includes("vendorbulkallocation") ||
    pathname.includes("vendorbulkunallocation") ||
    pathname.includes("deliveredTask") ||
    pathname.includes("exportjson")
  ) {
    return "projects";
  }

  if (pathname.includes("users") || pathname.includes("adduser")) {
    return "users";
  }

  if (pathname.includes("experts") || pathname.includes("addexpert")) {
    return "experts";
  }

  if (pathname.includes("vendors") || pathname.includes("addvendor")) {
    return "vendors";
  }

  if (pathname.includes("script-execution")) {
    return "script-execution";
  }

  return "projects";
};

const SidebarLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  useEffect(() => {
    if (location.pathname === "/") {
      navigate("/projects", { replace: true });
    }
  }, [location.pathname, navigate]);

  const activeKey = getActiveKey(location.pathname);

  const clearSessionCookies = (): void => {
    document.cookie = "token=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax";
    document.cookie = "refresh_token=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax";
  };

  const handleLogout = (): void => {
    clearSessionCookies();
    navigate("/new/login", { replace: true });
  };

  return (
    <div className={styles.layout}>
      <aside
        className={`${styles.sidebar} ${isSidebarCollapsed ? styles.sidebarCollapsed : ""}`.trim()}
      >
        <div className={styles.sidebarTopRow}>
          <button
            type="button"
            className={styles.collapseButton}
            onClick={() => setIsSidebarCollapsed((previousState) => !previousState)}
            aria-label={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isSidebarCollapsed ? (
              <KeyboardDoubleArrowRightRoundedIcon fontSize="small" />
            ) : (
              <KeyboardDoubleArrowLeftRoundedIcon fontSize="small" />
            )}
          </button>
        </div>

        <button
          type="button"
          className={styles.logoButton}
          onClick={() => navigate("/projects")}
          aria-label="Go to projects"
        >
          <Logo />
        </button>

        <nav className={styles.navigation} aria-label="Main navigation">
          {navItems.map((item) => {
            const isActive = item.key === activeKey;

            return (
              <Link
                key={item.key}
                to={item.to}
                className={`${styles.navLink} ${isActive ? styles.navLinkActive : ""}`.trim()}
                title={item.label}
              >
                <span className={styles.navIcon}>{item.icon}</span>
                <span className={styles.navLabel}>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <button type="button" className={styles.logoutButton} onClick={handleLogout}>
          <LogoutRoundedIcon fontSize="small" />
          <span className={styles.logoutText}>Logout</span>
        </button>
      </aside>

      <main className={styles.content}>
        <Outlet />
      </main>
    </div>
  );
};

export default SidebarLayout;
